import { Link, useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight, ClipboardCheck } from 'lucide-react'
import { useFiveSBoard } from '@/lib/queries'
import { useAuth } from '@/context/AuthContext'
import { useI18n, useT } from '@/i18n/I18nProvider'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import { Hint } from '@/components/Hint'
import {
  boardFilterCounts,
  boardFilterTest,
  defaultBoardFilter,
  groupByPlant,
  resolveBoardFilter,
  monthParam,
  parseMonthParam,
  sparkline,
  trendDelta,
  type BoardFilter,
} from '@/lib/fiveSBoard'
import { fiveSTarget, scoreColor } from '@/lib/fiveSTarget'
import { wallClock } from '@/lib/timezone'
import { cn, formatPercent } from '@/lib/utils'
import type { TranslationKey } from '@/i18n/dictionary'
import type { FiveSBoard, FiveSBoardMachine, FiveSBoardStatus } from '@/types/api'

/**
 * "Ovaj mjesec" — which machines still need a 5S walk, and how the walked
 * ones did.
 *
 * `/5s` opened on a list of walks already done, which answers the question
 * nobody standing in the hall is asking. This answers the other half: every
 * machine in the reader's plants, grouped by plant, the ones still due at the
 * top of each, with a button that starts the walk on that machine.
 *
 * Everything the URL can hold, it holds — the month (`?mjesec=2026-10`) and the
 * quick filter (`?prikaz=za-obilazak`) — so "the October board, only the ones
 * not walked" is a link somebody can send.
 *
 * Somebody who may not evaluate reads the same board without the buttons:
 * the scores of their machines, posted the way they are on the floor.
 */
export function FiveSMonthBoard() {
  const t = useT()
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()

  const month = parseMonthParam(params.get('mjesec'))
  const query = useFiveSBoard(month)

  /** Change one parameter, keep the rest — the tab lives in the same URL. */
  function setParam(name: string, value: string | null) {
    setParams(
      (current) => {
        const next = new URLSearchParams(current)

        if (value === null) next.delete(name)
        else next.set(name, value)

        return next
      },
      { replace: true },
    )
  }

  if (query.isPending) return <Spinner label={t('state.loading')} />

  if (query.isError) {
    return <ErrorState message={(query.error as Error).message} onRetry={() => query.refetch()} />
  }

  const board = query.data
  const counts = boardFilterCounts(board.workCenters)

  // The default chip is the one without a parameter, so `/5s` is always the
  // board as this reader would open it.
  const fallback = defaultBoardFilter(Boolean(user?.canEvaluate), counts.moje)
  const effective = resolveBoardFilter(params.get('prikaz'), fallback, counts.moje)
  const groups = groupByPlant(board.workCenters, boardFilterTest(effective))

  /*
   * Buttons only on the month that can still be walked. A walk is dated the
   * day it happens, so "Ocijeni" on October's board would file a November
   * walk while looking like it filled October's gap.
   */
  const canAct = Boolean(user?.canEvaluate) && board.period.isCurrent

  return (
    <div className="space-y-4">
      <CoverageCard
        board={board}
        // Dimmed while another month loads over the one on screen.
        stale={query.isPlaceholderData}
        // The current month is the one without a parameter, so its link is
        // plain `/5s` whichever way somebody arrived at it.
        onMonth={(next) => setParam('mjesec', next && !isThisMonth(next) ? monthParam(next) : null)}
      />

      {board.workCenters.length === 0 ? (
        <Card>
          <EmptyState
            icon={<ClipboardCheck className="size-8" aria-hidden />}
            title={t('fiveS.board.empty.title')}
            description={t('fiveS.board.empty.description')}
          />
        </Card>
      ) : (
        <>
          <FilterChips
            counts={counts}
            active={effective}
            onChange={(next) => setParam('prikaz', next === fallback ? null : next)}
          />

          {groups.length === 0 ? (
            <Card>
              <EmptyState
                title={emptyTitle(t, effective, board.period.label)}
                action={
                  <Button variant="secondary" onClick={() => setParam('prikaz', 'sve')}>
                    {t('fiveS.board.empty.showAll')}
                  </Button>
                }
              />
            </Card>
          ) : (
            groups.map((group) => (
              <PlantSection
                key={group.id}
                name={group.name}
                walked={group.walked}
                total={group.total}
                machines={group.machines}
                months={board.months}
                canAct={canAct}
              />
            ))
          )}
        </>
      )}
    </div>
  )
}

/** On the factory's calendar, which is what the server reads "this month" off. */
function isThisMonth(month: { year: number; month: number }): boolean {
  const now = wallClock()

  return month.year * 100 + month.month >= now.year * 100 + now.month
}

function emptyTitle(t: ReturnType<typeof useT>, filter: BoardFilter, month: string): string {
  if (filter === 'za-obilazak') return t('fiveS.board.empty.allWalked', { month })
  if (filter === 'obidjene') return t('fiveS.board.empty.noneWalked', { month })

  return t('fiveS.board.empty.mine')
}

// ---------------------------------------------------------------------------
// Coverage and the month
// ---------------------------------------------------------------------------

function CoverageCard({
  board,
  stale,
  onMonth,
}: {
  board: FiveSBoard
  stale: boolean
  onMonth: (month: { year: number; month: number } | null) => void
}) {
  const t = useT()
  const { tp } = useI18n()
  const { totals, period } = board
  const share = totals.machines > 0 ? (totals.walked / totals.machines) * 100 : 0

  return (
    <Card className={cn('space-y-3 transition-opacity', stale && 'opacity-60')}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {/* The month, with the way back to it from anywhere else. */}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            className="px-0 size-control"
            aria-label={t('fiveS.board.month.previous')}
            onClick={() => onMonth(board.previous)}
          >
            <ChevronLeft className="size-4" aria-hidden />
          </Button>
          <h2 className="min-w-32 text-center text-base font-semibold capitalize" aria-live="polite">
            {period.label}
          </h2>
          <Button
            variant="ghost"
            className="px-0 size-control"
            aria-label={t('fiveS.board.month.next')}
            disabled={!board.next}
            onClick={() => board.next && onMonth(board.next)}
          >
            <ChevronRight className="size-4" aria-hidden />
          </Button>
        </div>

        {!period.isCurrent && (
          <Button variant="secondary" onClick={() => onMonth(null)}>
            {t('fiveS.board.month.current')}
          </Button>
        )}
      </div>

      <div>
        <p className="font-heading text-2xl font-semibold tabular-nums">
          {totals.machines > 0
            ? tp('fiveS.board.coverage', totals.machines, { walked: totals.walked })
            : t('fiveS.board.coverage.none')}
        </p>

        {/* Machines walked, not the score: this bar is about whether the
            month's walking is done, which is a different question from how
            well the machines did. */}
        <div
          className="bg-muted mt-2 h-2.5 overflow-hidden rounded-full"
          role="progressbar"
          aria-label={t('fiveS.board.coverage.label')}
          aria-valuemin={0}
          aria-valuemax={totals.machines}
          aria-valuenow={totals.walked}
          aria-valuetext={formatPercent(totals.coveragePercentage ?? 0)}
        >
          <div className="bg-five-s h-full rounded-full transition-[width]" style={{ width: `${share}%` }} />
        </div>
      </div>

      <p className="muted flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span>
          {totals.due > 0 ? t('fiveS.board.left', { count: totals.due }) : t('fiveS.board.allDone')}
        </span>
        {totals.drafts > 0 && <span>{t('fiveS.board.drafts', { count: totals.drafts })}</span>}
        {totals.averagePercentage !== null && (
          <span>{t('fiveS.board.average', { value: formatPercent(totals.averagePercentage) })}</span>
        )}
        {totals.withoutTemplate > 0 && (
          <span className="inline-flex items-center gap-1">
            {t('fiveS.board.withoutTemplate', { count: totals.withoutTemplate })}
            <Hint>{t('fiveS.board.withoutTemplate.help')}</Hint>
          </span>
        )}
      </p>

      {!period.isCurrent && <p className="muted text-xs">{t('fiveS.board.past')}</p>}
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Quick filters
// ---------------------------------------------------------------------------

const FILTER_LABEL: Record<BoardFilter, TranslationKey> = {
  sve: 'fiveS.board.filter.all',
  'za-obilazak': 'fiveS.board.filter.due',
  obidjene: 'fiveS.board.filter.walked',
  moje: 'fiveS.board.filter.mine',
}

function FilterChips({
  counts,
  active,
  onChange,
}: {
  counts: Record<BoardFilter, number>
  active: BoardFilter
  onChange: (filter: BoardFilter) => void
}) {
  const t = useT()

  // "Moje" only for somebody who has machines of their own.
  const shown = (Object.keys(FILTER_LABEL) as BoardFilter[]).filter(
    (filter) => filter !== 'moje' || counts.moje > 0,
  )

  return (
    // Scrolls sideways on a phone rather than wrapping into a second row.
    <div className="-mx-1 overflow-x-auto px-1 pb-1">
      <div className="flex w-max gap-2" role="group" aria-label={t('fiveS.board.filter.aria')}>
        {shown.map((filter) => {
          const selected = filter === active

          return (
            <button
              key={filter}
              type="button"
              aria-pressed={selected}
              onClick={() => onChange(filter)}
              className={cn(
                'min-h-control inline-flex items-center gap-2 rounded-full border px-control-x text-sm font-medium transition-colors',
                selected
                  ? 'border-foreground bg-foreground text-background'
                  : 'bg-card/65 hover:bg-muted border-[var(--border-subtle)]',
              )}
            >
              {t(FILTER_LABEL[filter])}
              <span
                className={cn(
                  'rounded-full px-1.5 text-xs tabular-nums',
                  selected ? 'bg-background/20' : 'bg-muted',
                )}
              >
                {counts[filter]}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// One plant
// ---------------------------------------------------------------------------

function PlantSection({
  name,
  walked,
  total,
  machines,
  months,
  canAct,
}: {
  name: string
  walked: number
  total: number
  machines: FiveSBoardMachine[]
  months: FiveSBoard['months']
  canAct: boolean
}) {
  const t = useT()
  const headingId = `pogon-${name.replace(/\W+/g, '-')}`

  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <div className="flex items-baseline justify-between gap-3 px-1">
        <h2 id={headingId} className="text-base font-semibold">
          {name}
        </h2>
        <span className="muted text-sm tabular-nums">
          {t('fiveS.board.plant.progress', { walked, total })}
        </span>
      </div>

      {/*
        A table at a desk, cards on a phone. Both are rendered and one is
        hidden, rather than measured in a hook: the switch is the same
        `(pointer: fine)` and width the density tokens use, so the two can
        never disagree about which device this is.
      */}
      <Card className="hidden p-0 lg:pointer-fine:block">
        {/* Fixed columns, so the plants' tables line up one under another
            and read as one board rather than as separate lists. */}
        <Table className="table-fixed">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[28%] pl-card">{t('fiveS.board.col.machine')}</TableHead>
              <TableHead className="w-[13%]">{t('fiveS.board.col.status')}</TableHead>
              <TableHead className="w-[30%]">{t('fiveS.board.col.score')}</TableHead>
              <TableHead className="w-[17%]">{t('fiveS.board.col.trend')}</TableHead>
              <TableHead className="w-[12%] pr-card text-right">
                <span className="sr-only">{t('fiveS.board.col.action')}</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {machines.map((machine) => (
              <TableRow key={machine.id}>
                <TableCell className="pl-card">
                  <MachineName machine={machine} />
                </TableCell>
                <TableCell>
                  <StatusChip status={machine.status} />
                </TableCell>
                <TableCell>
                  <ScoreSummary machine={machine} />
                </TableCell>
                <TableCell>
                  <TrendCell trend={machine.trend} months={months} />
                </TableCell>
                <TableCell className="pr-card text-right">
                  <RowAction machine={machine} canAct={canAct} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ul className="space-y-2 lg:pointer-fine:hidden">
        {machines.map((machine) => (
          <li key={machine.id}>
            <Card className="space-y-2">
              <div className="flex items-start justify-between gap-3">
                <MachineName machine={machine} />
                <StatusChip status={machine.status} />
              </div>

              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <ScoreSummary machine={machine} />
                <TrendCell trend={machine.trend} months={months} />
              </div>

              <RowAction machine={machine} canAct={canAct} fullWidth />
            </Card>
          </li>
        ))}
      </ul>
    </section>
  )
}

function MachineName({ machine }: { machine: FiveSBoardMachine }) {
  const t = useT()

  return (
    <div className="min-w-0">
      <p className="truncate font-medium">{machine.name}</p>
      <p className="muted truncate text-xs">
        {machine.code}
        {machine.isMine && (
          <>
            {' · '}
            <span className="text-foreground font-medium">{t('fiveS.board.mine')}</span>
          </>
        )}
      </p>
    </div>
  )
}

const STATUS: Record<FiveSBoardStatus, { key: TranslationKey; color: string }> = {
  due: { key: 'fiveS.board.status.due', color: 'amber' },
  draft: { key: 'fiveS.board.status.draft', color: 'sky' },
  walked: { key: 'fiveS.board.status.walked', color: 'emerald' },
  noTemplate: { key: 'fiveS.board.status.noTemplate', color: 'slate' },
}

/** The state in words, never colour alone. */
export function StatusChip({ status }: { status: FiveSBoardStatus }) {
  const t = useT()

  return (
    <Badge color={STATUS[status].color} className="shrink-0">
      {t(STATUS[status].key)}
    </Badge>
  )
}

/**
 * A percentage in its band's colour — next to the number, so the colour only
 * repeats what is written. Links to the walk when there is one to open.
 */
export function ScoreChip({ value, to, label }: { value: number; to?: string; label?: string }) {
  const chip = (
    <Badge color={scoreColor(value)} className="tabular-nums">
      {formatPercent(value)}
    </Badge>
  )

  if (!to) return chip

  return (
    <Link
      to={to}
      aria-label={label}
      className="inline-flex rounded-full focus-visible:outline-2 focus-visible:outline-offset-2"
    >
      {chip}
    </Link>
  )
}

/**
 * This month's score when there is one; otherwise what the machine was last
 * seen at, so an unwalked machine is not a blank row.
 */
function ScoreSummary({ machine }: { machine: FiveSBoardMachine }) {
  const t = useT()

  if (machine.walk) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <ScoreChip
          value={machine.walk.scorePercentage}
          to={`/5s/${machine.walk.id}`}
          label={t('fiveS.board.openWalk', { name: machine.name })}
        />
        <span className="muted text-xs">
          {machine.walk.evaluatedOnLabel}
          {machine.walk.walks > 1 && ` · ${t('fiveS.board.walks', { count: machine.walk.walks })}`}
        </span>
      </div>
    )
  }

  if (machine.last) {
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="muted text-xs">{t('fiveS.board.lastSeen')}</span>
        <ScoreChip
          value={machine.last.scorePercentage}
          to={`/5s/${machine.last.id}`}
          label={t('fiveS.board.openWalk', { name: machine.name })}
        />
        <span className="muted text-xs">{machine.last.label}</span>
      </div>
    )
  }

  return <span className="muted text-xs">{t('fiveS.board.never')}</span>
}

function TrendCell({ trend, months }: { trend: (number | null)[]; months: FiveSBoard['months'] }) {
  return (
    <div className="flex items-center gap-2">
      <ScoreTrend trend={trend} months={months} />
      <TrendDelta value={trendDelta(trend)} />
    </div>
  )
}

/**
 * Six months as a line the width of a thumb.
 *
 * 0–100 always, with the company's target drawn as a dotted line, so a
 * machine hugging 80 % and one hugging 30 % do not look alike. Gaps are gaps: a month nobody
 * walked breaks the line rather than being bridged.
 */
export function ScoreTrend({
  trend,
  months,
}: {
  trend: (number | null)[]
  months: { label: string }[]
}) {
  const t = useT()
  const width = 64
  const height = 24
  const pad = 3
  const runs = sparkline(trend, width, height, pad)
  const points = runs.flat()
  const targetY = pad + (1 - fiveSTarget() / 100) * (height - pad * 2)

  const label = t('fiveS.board.trend.aria', {
    points: trend
      .map((value, index) =>
        `${months[index]?.label ?? ''}: ${value === null ? t('fiveS.board.trend.none') : formatPercent(value)}`,
      )
      .join(', '),
  })

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-label={label}
      className="text-five-s shrink-0 overflow-visible"
    >
      <line
        x1={0}
        x2={width}
        y1={targetY}
        y2={targetY}
        stroke="var(--border)"
        strokeDasharray="2 2"
      />
      {runs.map((run, index) =>
        run.length > 1 ? (
          <polyline
            key={index}
            points={run.map((point) => `${point.x},${point.y}`).join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth={1.75}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null,
      )}
      {points.map((point, index) => (
        <circle
          key={index}
          cx={point.x}
          cy={point.y}
          r={index === points.length - 1 ? 2.75 : 1.75}
          fill="currentColor"
        />
      ))}
    </svg>
  )
}

/** Up, down or level since the previous walked month — an arrow and a number, not a colour. */
export function TrendDelta({ value }: { value: number | null }) {
  const t = useT()

  if (value === null) return null

  const amount = formatPercent(Math.abs(value))
  const label =
    value > 0
      ? t('fiveS.board.delta.up', { value: amount })
      : value < 0
        ? t('fiveS.board.delta.down', { value: amount })
        : t('fiveS.board.delta.same')

  return (
    <span
      className={cn(
        'inline-flex items-center text-xs font-medium tabular-nums whitespace-nowrap',
        value > 0 && 'text-emerald-700 dark:text-emerald-400',
        value < 0 && 'text-amber-700 dark:text-amber-400',
        value === 0 && 'muted',
      )}
      title={label}
    >
      <span aria-hidden>
        {value > 0 ? '▲' : value < 0 ? '▼' : '='} {value === 0 ? '' : amount}
      </span>
      <span className="sr-only">{label}</span>
    </span>
  )
}

/**
 * The one thing to do with this machine, named by what it does.
 *
 * "Ocijeni" for one not walked yet, "Nastavi" for one somebody started. A
 * walked machine needs nothing — its score chip already opens the walk.
 */
function RowAction({
  machine,
  canAct,
  fullWidth = false,
}: {
  machine: FiveSBoardMachine
  canAct: boolean
  fullWidth?: boolean
}) {
  const t = useT()

  if (!canAct) return null

  if (machine.status === 'draft' && machine.draft) {
    return (
      <Button
        variant="secondary"
        fullWidth={fullWidth}
        nativeButton={false}
        render={<Link to={`/5s/${machine.draft.id}/izmjena`} />}
        aria-label={t('fiveS.board.continueMachine', { name: machine.name })}
      >
        {t('fiveS.board.continue')}
      </Button>
    )
  }

  if (machine.status === 'due') {
    return (
      <Button
        fullWidth={fullWidth}
        nativeButton={false}
        render={<Link to={`/5s/nova?workCenterId=${machine.id}`} />}
        aria-label={t('fiveS.board.evaluateMachine', { name: machine.name })}
        // The 5S green: the colour of "start a 5S walk" wherever it is offered.
        className="bg-five-s text-five-s-foreground hover:bg-five-s/90"
      >
        <ClipboardCheck className="size-4" aria-hidden />
        {t('fiveS.board.evaluate')}
      </Button>
    )
  }

  return null
}
